/**
 * Flow device action creation.
 * Duplicated from Scenes for Flow independence.
 */

import { get } from 'svelte/store';
import { devices } from 'src/stores';
import { createHexToDecimalOrId } from 'src/helpers/utilities';
import { isZendure, isDiffuser, isPill } from 'src/helpers/interface_helpers';
import { createGuiDeviceId } from './utilities';
import type { DeviceActionUI } from 'src/types/DeviceActionUI';
import { is_device_action_ui } from 'src/types/DeviceActionUI';
import { prepareVCActionToIots } from './device_actions/custom_cases/virtual_device_actions';
import { prepareRelayAction, isRelayLikeMode } from './device_actions/relay_actions';
import { prepareZendureAction } from './device_actions/custom_cases/zendure_actions';
import { prepareCuryAction } from './device_actions/custom_cases/cury_actions';
import { prepareMediaPlayerAction } from './device_actions/custom_cases/media_player_actions';
import {
	prepareColorBulbAction,
	prepareCCTBulbAction,
	isColorBulb,
	isCCTMode,
} from './device_actions/bulb_actions';
import { prepareDimmerAction } from './device_actions/dimmer_actions';
import type {
	DeviceActionContext,
	DeviceActionStrategyArgs,
	DeviceActionStrategyResult,
	PreparedDeviceAction,
} from './device_actions/types';
import type { DeviceAction } from 'src/types/DeviceActionUI';

const isVCType = (type: string): boolean =>
	['button', 'number', 'boolean', 'enum', 'text'].includes(type);

interface DeviceActionStrategy {
	name: string;
	canHandle: (args: DeviceActionStrategyArgs) => boolean;
	prepare: (args: DeviceActionStrategyArgs) => DeviceActionStrategyResult;
	postProcess?: (
		result: Exclude<DeviceActionStrategyResult, null | undefined>,
		args: DeviceActionStrategyArgs,
	) => DeviceActionStrategyResult;
}

const applyRelayTimer = (
	action: PreparedDeviceAction,
	element: DeviceActionUI,
): PreparedDeviceAction => {
	if (Number(element?.timer?.value) > 0) {
		action.turntimer = Number(element.timer.value);
		action.turntimer_unit = element.timer.units;
	}
	return action;
};

const DEVICE_ACTION_STRATEGIES: DeviceActionStrategy[] = [
	{
		name: 'virtual-component',
		canHandle: ({ primaryAction }) => isVCType(primaryAction?.name ?? ''),
		prepare: ({ element, context, deviceID, hexToDecimal }) => {
			let decimalId: number | string;
			if (typeof hexToDecimal === 'number') {
				decimalId = hexToDecimal;
			} else {
				const converted = Number(hexToDecimal);
				decimalId = Number.isFinite(converted) ? converted : hexToDecimal;
			}
			if (typeof decimalId !== 'number' && typeof decimalId !== 'string') {
				return undefined;
			}
			return prepareVCActionToIots(deviceID, decimalId, context.actions);
		},
	},
	{
		name: 'pill',
		canHandle: ({ type }) => isPill(type),
		prepare: ({ context }) => prepareRelayAction(context),
	},
	{
		name: 'relay-like',
		canHandle: ({ mode }) => isRelayLikeMode(mode ?? ''),
		prepare: ({ context }) => prepareRelayAction(context),
		postProcess: (result, { element }) => applyRelayTimer(result as PreparedDeviceAction, element),
	},
	{
		name: 'zendure',
		canHandle: ({ type }) => isZendure(type ?? ''),
		prepare: ({ context }) => prepareZendureAction(context),
	},
	{
		name: 'cury-diffuser',
		canHandle: ({ type }) => isDiffuser(type ?? ''),
		prepare: ({ context }) => prepareCuryAction(context),
	},
	{
		name: 'media-player',
		canHandle: ({ mode }) => mode === 'media_player',
		prepare: ({ context }) => prepareMediaPlayerAction(context),
	},
	{
		name: 'color-bulb',
		canHandle: ({ type, mode, context }) => isColorBulb(type ?? '', mode ?? '', context.channelMode),
		prepare: ({ context }) => prepareColorBulbAction(context),
	},
	{
		name: 'cct-bulb',
		canHandle: ({ type, mode, context }) => isCCTMode(type ?? '', context.channelMode, mode),
		prepare: ({ context }) => prepareCCTBulbAction(context),
	},
	{
		name: 'dimmer-default',
		canHandle: () => true,
		prepare: ({ context }) => prepareDimmerAction(context),
	},
];

export const delay_action = (
	obj: Record<string, unknown>,
	delayData: { value: number; units: string },
	more?: Record<string, unknown>,
): Record<string, unknown> => {
	const objDelayedAction: Record<string, unknown> = {
		wait: delayData.value,
		wid: Date.now(),
		do: { blk: [obj] },
		units: delayData.units === 'min' ? 'minutes' : delayData.units,
	};

	if (more !== undefined) {
		objDelayedAction.do = {
			blk: [{ ...obj, msg_type: more.msg_type }],
		};
		Object.assign(objDelayedAction, {
			_gui_function: more._gui_function,
			msg_type: more.msg_type,
		});
	}

	return objDelayedAction;
};

/**
 * Converts a device action from Flow node data into an IoT-ready payload.
 */
export const createDeviceAction = (
	element: DeviceActionUI,
	type: string | undefined,
	_mode: string | undefined,
): DeviceActionStrategyResult => {
	if (!is_device_action_ui(element) || typeof type === 'undefined') {
		return undefined;
	}

	const rawChannel = element.device.channel ?? 0;
	const channelNumber = Number(rawChannel);
	const channel = Number.isFinite(channelNumber) ? channelNumber : 0;
	const hex_id = element.device.id;
	const deviceID = createGuiDeviceId(element.device);
	const channelMode = get(devices)?.[deviceID]?.mode;
	const hexToDecimal = createHexToDecimalOrId(hex_id);
	const action_arr = element.actions || [];
	const _action_el: DeviceAction = action_arr[0];

	if (!action_arr.length || !_action_el) {
		return undefined;
	}

	const isVirtualComponentAction = isVCType(_action_el?.name ?? '');

	if (!isVirtualComponentAction && typeof _mode === 'undefined') {
		return undefined;
	}

	const context: DeviceActionContext = {
		hexToDecimal,
		channel,
		actions: action_arr,
		mode: _mode,
		type,
		channelMode,
	};

	const strategyArgs: DeviceActionStrategyArgs = {
		element,
		type,
		mode: _mode ?? '',
		context,
		primaryAction: _action_el,
		deviceID,
		hexToDecimal,
	};

	let action: DeviceActionStrategyResult;

	for (const strategy of DEVICE_ACTION_STRATEGIES) {
		if (!strategy.canHandle(strategyArgs)) continue;

		const result = strategy.prepare(strategyArgs);
		if (result) {
			action = strategy.postProcess ? strategy.postProcess(result, strategyArgs) : result;
		} else {
			action = result;
		}
		break;
	}

	if (!action) return undefined;

	if (element.delay != null && Number(element.delay.value) > 0) {
		return delay_action(
			action as Record<string, unknown>,
			element.delay as { value: number; units: string },
		) as DeviceActionStrategyResult;
	}

	return action;
};
